/**
 * Homepage carousels — pijltjes + optionele autoplay + optionele infinite loop (forward).
 * data-uf-home-carousel op root; items via data-carousel-item (default .uf-home-carousel__item).
 * data-uf-carousel-loop: clone start slides at end; na laatste slide geen terugspoel, maar naadloze reset.
 */
(function () {
  'use strict';

  function initCarousel(root) {
    if (!root || root.getAttribute('data-uf-carousel-bound') === '1') return;

    var viewport = root.querySelector('[data-uf-carousel-viewport]');
    var track = root.querySelector('[data-uf-carousel-track]');
    var prevBtn = root.querySelector('[data-uf-carousel-prev]');
    var nextBtn = root.querySelector('[data-uf-carousel-next]');
    if (!viewport || !track || !prevBtn || !nextBtn) return;

    var itemSelector = root.getAttribute('data-carousel-item') || '.uf-home-carousel__item';
    var loopMode = root.hasAttribute('data-uf-carousel-loop');
    var autoplayMs = parseInt(root.getAttribute('data-carousel-autoplay'), 10);
    if (isNaN(autoplayMs) || autoplayMs < 1500) autoplayMs = 0;
    var autoplayDelayMs = parseInt(root.getAttribute('data-carousel-autoplay-delay'), 10);
    if (isNaN(autoplayDelayMs) || autoplayDelayMs < 0) {
      autoplayDelayMs = autoplayMs ? Math.min(2000, autoplayMs) : 0;
    }

    root.setAttribute('data-uf-carousel-bound', '1');

    var index = 0;
    var resizeTimer = null;
    var remeasureTimer = null;
    var autoplayTimer = null;
    var autoplayTimeout = null;
    var lastViewportWidth = 0;
    var loopOriginalCount = 0;
    var loopMaxIndex = 0;
    var loopSnapping = false;
    var loopSnapTimer = null;
    var prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var dotsRoot = root.querySelector('[data-uf-carousel-dots]');
    var swipePointerId = null;
    var swipeStartX = 0;
    var swipeDeltaX = 0;
    var swipeThreshold = 48;

    function isLegserviceCarousel() {
      return root.classList.contains('uf-legservice__carousel');
    }

    function isPopularCarousel() {
      return root.classList.contains('uf-home-popular-carousel') || root.hasAttribute('data-uf-home-popular-carousel');
    }

    function setStaticState(isStatic) {
      if (isLegserviceCarousel()) {
        root.classList.toggle('uf-legservice__carousel--static', isStatic);
      }
      if (isPopularCarousel()) {
        root.classList.toggle('uf-home-popular-carousel--static', isStatic);
      }
      if (root.classList.contains('uf-home-reviews-slider')) {
        root.classList.toggle('uf-home-reviews-slider--static', isStatic);
      }
    }

    function getActiveDotIndex() {
      if (loopMode && loopOriginalCount > 0) {
        return index % loopOriginalCount;
      }
      return index;
    }

    function updateDots() {
      if (!dotsRoot) return;
      var active = getActiveDotIndex();
      dotsRoot.querySelectorAll('[data-uf-carousel-dot]').forEach(function (dot) {
        var dotIndex = parseInt(dot.getAttribute('data-uf-carousel-dot-index'), 10);
        var isActive = dotIndex === active;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    if (dotsRoot) {
      dotsRoot.querySelectorAll('[data-uf-carousel-dot]').forEach(function (dot) {
        dot.addEventListener('click', function () {
          var dotIndex = parseInt(dot.getAttribute('data-uf-carousel-dot-index'), 10);
          if (isNaN(dotIndex)) return;
          index = dotIndex;
          update();
        });
      });
    }

    function getItems() {
      return track.querySelectorAll(itemSelector);
    }

    function getRealItems() {
      return track.querySelectorAll(itemSelector + ':not([data-uf-carousel-clone])');
    }

    function removeLoopClones() {
      track.querySelectorAll(itemSelector + '[data-uf-carousel-clone]').forEach(function (el) {
        el.remove();
      });
    }

    function setupLoopClones() {
      removeLoopClones();
      loopOriginalCount = 0;
      loopMaxIndex = 0;
      if (!loopMode) return;

      var realItems = getRealItems();
      loopOriginalCount = realItems.length;
      if (loopOriginalCount < 2) return;

      var visible = getVisible();
      var cloneCount = Math.min(visible, loopOriginalCount);

      for (var ci = 0; ci < cloneCount; ci++) {
        var clone = realItems[ci].cloneNode(true);
        clone.setAttribute('data-uf-carousel-clone', 'end');
        clone.setAttribute('aria-hidden', 'true');
        clone.tabIndex = -1;
        track.appendChild(clone);
      }

      loopMaxIndex = loopOriginalCount;
    }

    function readCssInt(el, prop, fallback) {
      var n = parseInt(getComputedStyle(el).getPropertyValue(prop), 10);
      return isNaN(n) || n < 1 ? fallback : n;
    }

    function getVisible() {
      return readCssInt(root, '--uf-carousel-visible', 4);
    }

    function getRows() {
      return readCssInt(root, '--uf-carousel-rows', 1);
    }

    function getStepCols() {
      return readCssInt(root, '--uf-carousel-step-cols', 1);
    }

    function getGap() {
      var style = getComputedStyle(track);
      return parseFloat(style.columnGap || style.gap) || 0;
    }

    function setSlideWidths() {
      var visible = getVisible();
      var gap = getGap();
      var width = viewport.clientWidth;

      if (width < 40) {
        return lastViewportWidth > 0 ? lastViewportWidth : 1;
      }

      viewport.style.removeProperty('--uf-carousel-slide-px');

      /* Hele pixels: voorkomt wazige tekst bij translate3d na de eerste slide */
      var slide = Math.floor((width - gap * (visible - 1)) / visible);

      if (slide < 1) slide = 1;

      viewport.style.setProperty('--uf-carousel-slide-px', slide + 'px');
      lastViewportWidth = slide + gap;
      return lastViewportWidth;
    }

    function remeasureCarousel(resetAutoplay) {
      clearTimeout(remeasureTimer);
      remeasureTimer = setTimeout(function () {
        if (!root.isConnected) return;

        var width = viewport.clientWidth;
        if (width < 40) return;

        var stepBefore = lastViewportWidth;
        if (loopMode) setupLoopClones();
        setSlideWidths();

        if (!resetAutoplay && stepBefore > 0 && Math.abs(lastViewportWidth - stepBefore) < 2) {
          return;
        }

        if (loopMode && index > loopMaxIndex) index = loopMaxIndex;
        applyTransform(false);
        update(resetAutoplay);
      }, 50);
    }

    function getSlideStep() {
      setSlideWidths();
      var slide = parseFloat(getComputedStyle(viewport).getPropertyValue('--uf-carousel-slide-px'));
      if (!slide || isNaN(slide)) {
        var items = getItems();
        if (!items.length) return 0;
        return items[0].offsetWidth + getGap();
      }
      return slide + getGap();
    }

    function getTotalColumns() {
      var items = loopMode ? getRealItems() : getItems();
      var rows = getRows();
      if (!items.length) return 0;
      return Math.ceil(items.length / rows);
    }

    function getMaxIndex() {
      if (loopMode && loopMaxIndex > 0) return loopMaxIndex;
      var visible = getVisible();
      var cols = getTotalColumns();
      return Math.max(0, cols - visible);
    }

    function clampIndex() {
      var max = getMaxIndex();
      if (index > max) index = max;
      if (index < 0) index = 0;
    }

    function applyTransform(animate) {
      var step = getSlideStep();
      var offset = Math.round(-index * step);
      if (animate === false || prefersReducedMotion) {
        track.style.transition = 'none';
      }
      track.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
      if (animate === false || prefersReducedMotion) {
        void track.offsetHeight;
        track.style.transition = '';
      }
    }

    function snapLoopToStart() {
      if (loopSnapTimer) {
        clearTimeout(loopSnapTimer);
        loopSnapTimer = null;
      }
      loopSnapping = true;
      index = 0;
      applyTransform(false);
      loopSnapping = false;
    }

    function scheduleLoopSnap() {
      if (!loopMode || loopMaxIndex <= 0 || index < loopMaxIndex) return;
      if (loopSnapTimer) clearTimeout(loopSnapTimer);
      if (prefersReducedMotion) {
        snapLoopToStart();
        return;
      }
      loopSnapTimer = setTimeout(function () {
        loopSnapTimer = null;
        if (index >= loopMaxIndex) snapLoopToStart();
      }, 420);
    }

    function onLoopTransitionEnd(e) {
      if (!loopMode || loopSnapping || loopMaxIndex <= 0) return;
      if (e.target !== track) return;
      if (e.propertyName && e.propertyName !== 'transform') return;
      if (index >= loopMaxIndex) snapLoopToStart();
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
      if (autoplayTimeout) {
        clearTimeout(autoplayTimeout);
        autoplayTimeout = null;
      }
    }

    function autoplayTick() {
      var stepCols = getStepCols();
      if (loopMode && loopMaxIndex > 0) {
        if (index >= loopMaxIndex) {
          snapLoopToStart();
          return;
        }
        index = Math.min(loopMaxIndex, index + stepCols);
        update(false);
        return;
      }

      var maxIdx = getMaxIndex();
      if (maxIdx <= 0) return;
      if (index >= maxIdx) index = 0;
      else index = Math.min(maxIdx, index + stepCols);
      update(false);
    }

    function startAutoplay() {
      stopAutoplay();
      if (!autoplayMs || prefersReducedMotion) return;

      if (loopMode) {
        if (loopMaxIndex <= 0) return;
      } else {
        var max = getMaxIndex();
        if (max <= 0) return;
      }

      var delay = Math.min(autoplayDelayMs, autoplayMs);

      autoplayTimeout = setTimeout(function () {
        autoplayTimeout = null;
        autoplayTick();
        autoplayTimer = setInterval(autoplayTick, autoplayMs);
      }, delay);
    }

    function update(resetAutoplay) {
      if (resetAutoplay !== false) startAutoplay();

      clampIndex();
      var items = loopMode ? getRealItems() : getItems();
      var visible = getVisible();
      var rows = getRows();
      var max = getMaxIndex();

      if (items.length <= visible * rows) {
        setStaticState(true);
        track.style.transform = 'translate3d(0, 0, 0)';
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        stopAutoplay();
        updateDots();
        return;
      }

      setStaticState(false);
      applyTransform(true);
      scheduleLoopSnap();
      prevBtn.disabled = index <= 0;
      nextBtn.disabled = loopMode ? false : index >= max;
      root.setAttribute('aria-roledescription', 'carousel');
      viewport.setAttribute('aria-live', 'polite');
      updateDots();
    }

    prevBtn.addEventListener('click', function () {
      var stepCols = getStepCols();
      if (index > 0) {
        index = Math.max(0, index - stepCols);
        update();
      }
    });

    nextBtn.addEventListener('click', function () {
      var stepCols = getStepCols();
      if (loopMode && loopMaxIndex > 0) {
        if (index >= loopMaxIndex) {
          snapLoopToStart();
          update();
          return;
        }
        index = Math.min(loopMaxIndex, index + stepCols);
        update();
        return;
      }

      var max = getMaxIndex();
      if (index < max) {
        index = Math.min(max, index + stepCols);
        update();
      }
    });

    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) startAutoplay();
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        index = 0;
        lastViewportWidth = 0;
        remeasureCarousel(true);
      }, 120);
    });

    if (typeof ResizeObserver !== 'undefined') {
      var ro = new ResizeObserver(function () {
        remeasureCarousel(false);
      });
      ro.observe(viewport);
    }

    if (typeof IntersectionObserver !== 'undefined') {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              lastViewportWidth = 0;
              remeasureCarousel(false);
            }
          });
        },
        { root: null, threshold: 0.05 }
      );
      io.observe(viewport);
    }

    if (loopMode) {
      track.addEventListener('transitionend', onLoopTransitionEnd);
      setupLoopClones();
    }

    function swipeAllowed() {
      return getVisible() === 1 && getRows() === 1;
    }

    function finishSwipe() {
      swipePointerId = null;
      track.style.transition = '';
      if (Math.abs(swipeDeltaX) >= swipeThreshold) {
        var stepCols = getStepCols();
        if (swipeDeltaX < 0) {
          if (loopMode && loopMaxIndex > 0) {
            if (index >= loopMaxIndex) snapLoopToStart();
            else index = Math.min(loopMaxIndex, index + stepCols);
          } else {
            var max = getMaxIndex();
            if (index < max) index = Math.min(max, index + stepCols);
          }
        } else if (swipeDeltaX > 0 && index > 0) {
          index = Math.max(0, index - stepCols);
        } else {
          applyTransform(true);
          swipeDeltaX = 0;
          startAutoplay();
          return;
        }
        update();
      } else {
        applyTransform(true);
        startAutoplay();
      }
      swipeDeltaX = 0;
    }

    viewport.addEventListener('pointerdown', function (e) {
      if (!swipeAllowed()) return;
      if (getMaxIndex() <= 0 && !loopMode) return;
      swipePointerId = e.pointerId;
      swipeStartX = e.clientX;
      swipeDeltaX = 0;
      stopAutoplay();
      try {
        viewport.setPointerCapture(swipePointerId);
      } catch (err) {}
    });

    viewport.addEventListener('pointermove', function (e) {
      if (swipePointerId == null || e.pointerId !== swipePointerId) return;
      swipeDeltaX = e.clientX - swipeStartX;
      if (Math.abs(swipeDeltaX) <= 8) return;
      var step = getSlideStep();
      if (!step) return;
      track.style.transition = 'none';
      track.style.transform =
        'translate3d(' + Math.round(-index * step + swipeDeltaX) + 'px, 0, 0)';
    });

    viewport.addEventListener('pointerup', function (e) {
      if (swipePointerId == null || e.pointerId !== swipePointerId) return;
      try {
        viewport.releasePointerCapture(swipePointerId);
      } catch (err) {}
      finishSwipe();
    });

    viewport.addEventListener('pointercancel', function (e) {
      if (swipePointerId == null || e.pointerId !== swipePointerId) return;
      finishSwipe();
    });

    update();
  }

  function initAll(scope) {
    var parent = scope && scope.nodeType === 1 ? scope : document;
    var selector = '[data-uf-home-carousel], [data-uf-home-popular-carousel]';
    if (scope && scope.nodeType === 1 && scope.matches && scope.matches(selector)) {
      initCarousel(scope);
    }
    parent.querySelectorAll(selector).forEach(initCarousel);
    initPopularProductCardCarousels(parent);
  }

  function uniq(arr) {
    var out = [];
    for (var i = 0; i < arr.length; i++) {
      if (arr[i] && out.indexOf(arr[i]) === -1) out.push(arr[i]);
    }
    return out;
  }

  function preloadImage(src) {
    return new Promise(function (resolve) {
      if (!src) return resolve();
      var img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.decoding = 'async';
      img.src = src;
    });
  }

  function waitForImage(imgEl) {
    return new Promise(function (resolve) {
      if (!imgEl) return resolve();
      if (imgEl.complete && imgEl.naturalWidth > 0) return resolve();
      function done() {
        imgEl.removeEventListener('load', done);
        imgEl.removeEventListener('error', done);
        resolve();
      }
      imgEl.addEventListener('load', done);
      imgEl.addEventListener('error', done);
    });
  }

  function setupPopularProductCardCarousel(card) {
    if (!card || card.getAttribute('data-uf-card-carousel-bound') === '1') return null;
    card.setAttribute('data-uf-card-carousel-bound', '1');

    var firstImg = card.querySelector('.grid__image .first--image') || card.querySelector('.grid__image img');
    if (!firstImg) return null;

    var secondImg = card.querySelector('.grid__image .second--image');
    var prevBtn = card.querySelector('.uf-card-nav--prev');
    var nextBtn = card.querySelector('.uf-card-nav--next');

    var mainSrc = firstImg.getAttribute('data-primary-src') || firstImg.getAttribute('data-src') || firstImg.getAttribute('src') || '';
    var hoverSrc = secondImg ? (secondImg.getAttribute('data-src') || secondImg.getAttribute('src') || '') : '';
    var raw = (card.getAttribute('data-product-images') || '').split('||');
    var rawU = uniq(
      raw.filter(function (s) {
        return s && String(s).trim();
      })
    );
    var extraSources;
    if (rawU.length === 2) {
      extraSources = rawU.slice();
    } else {
      extraSources = rawU.filter(function (src) {
        return src && src !== mainSrc && src !== hoverSrc;
      });
      if (!extraSources.length && hoverSrc && hoverSrc !== mainSrc) {
        extraSources = [mainSrc, hoverSrc];
      } else if (!extraSources.length && hoverSrc) {
        extraSources = [hoverSrc];
      }
    }

    if (!extraSources.length && !hoverSrc) return null;

    var state = {
      index: 0,
      isHoverFrame: false,
      carouselActive: false,
      main: mainSrc,
      hover: hoverSrc,
      list: extraSources
    };

    function render() {
      var src = '';
      if (state.isHoverFrame && state.hover) src = state.hover;
      else if (state.carouselActive && state.list.length) src = state.list[state.index % state.list.length];
      else src = state.main;
      if (!src) return;
      firstImg.setAttribute('src', src);
      firstImg.setAttribute('data-src', src);
    }

    function stopLinkNav(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        stopLinkNav(e);
        if (!state.list.length) return;
        state.carouselActive = true;
        if (state.isHoverFrame) {
          state.isHoverFrame = false;
          state.index = state.list.length - 1;
        }
        state.index = (state.index - 1 + state.list.length) % state.list.length;
        render();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        stopLinkNav(e);
        if (!state.list.length) return;
        state.carouselActive = true;
        if (state.isHoverFrame) {
          state.isHoverFrame = false;
          state.index = state.list.length - 1;
        }
        state.index = (state.index + 1) % state.list.length;
        render();
      });
    }

    card.addEventListener('mouseenter', function () {
      state.isHoverFrame = !!state.hover;
      render();
    });

    card.addEventListener('mouseleave', function () {
      state.isHoverFrame = false;
      state.carouselActive = false;
      state.index = 0;
      firstImg.setAttribute('src', state.main);
      firstImg.setAttribute('data-src', state.main);
    });

    return {
      firstImg: firstImg,
      mainSrc: mainSrc,
      hoverSrc: hoverSrc,
      extraSources: extraSources
    };
  }

  function initPopularProductCardCarousels(scope) {
    var parent = scope && scope.nodeType === 1 ? scope : document;
    var cards = Array.prototype.slice.call(
      parent.querySelectorAll('.uf-home-popular .uf-home-product[data-product-images]')
    );
    if (!cards.length) return;

    var states = [];
    for (var i = 0; i < cards.length; i++) {
      var st = setupPopularProductCardCarousel(cards[i]);
      if (st) states.push(st);
    }
    if (!states.length) return;

    Promise.all(
      cards.map(function (card) {
        return waitForImage(card.querySelector('.grid__image .first--image') || card.querySelector('.grid__image img'));
      })
    )
      .then(function () {
        return Promise.all(states.map(function (s) { return preloadImage(s.hoverSrc); }));
      })
      .then(function () {
        var queue = [];
        states.forEach(function (s) {
          (s.extraSources || []).forEach(function (src) { queue.push(src); });
        });
        queue = uniq(queue);

        var idx = 0;
        var active = 0;
        var maxConcurrent = 4;

        function pump() {
          while (active < maxConcurrent && idx < queue.length) {
            var src = queue[idx++];
            active++;
            preloadImage(src).then(function () {
              active--;
              pump();
            });
          }
        }
        setTimeout(pump, 60);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });

  window.UFHomeCarousel = {
    init: function (scope) {
      initAll(scope);
    }
  };
})();
